// Poster image for a RedGifs clip: our /api proxy first, then straight from RedGifs via the
// browser if the proxy fails (see redgifs-client.ts). Video can't do this — browsers block
// RedGifs video played from another site — so it stays on the proxy.
import { useEffect, useState } from 'react';
import { getRedGifsId } from '@/lib/utils';
import { cachedRedGifs, isProxyBlocked, markProxyBlocked, resolveRedGifs, type RedGifsMedia } from '@/lib/redgifs-client';

type Stage = 'proxy' | 'resolving' | 'direct' | 'failed';
interface Source { key: string; src: string | null; stage: Stage }

const start = (url: string | null | undefined, pick: (m: RedGifsMedia) => string | null, proxy: (id: string) => string): Source => {
  const id = getRedGifsId(url);
  const key = url || '';
  if (!id) return { key, src: null, stage: 'failed' };
  const cached = cachedRedGifs(id, url);
  if (cached && pick(cached)) return { key, src: pick(cached), stage: 'direct' };
  if (isProxyBlocked()) return { key, src: null, stage: 'resolving' };
  return { key, src: proxy(id), stage: 'proxy' };
};

const useRedGifsSource = (url: string | null | undefined, pick: (m: RedGifsMedia) => string | null, proxy: (id: string) => string) => {
  const [source, setSource] = useState<Source>(() => start(url, pick, proxy));
  const current = source.key === (url || '') ? source : start(url, pick, proxy);
  if (current !== source) setSource(current);

  useEffect(() => {
    if (current.stage !== 'resolving') return;
    const id = getRedGifsId(url);
    let live = true;
    (id ? resolveRedGifs(id, url) : Promise.resolve(null)).then(media => {
      if (!live) return;
      const src = media ? pick(media) : null;
      setSource({ key: url || '', src, stage: src ? 'direct' : 'failed' });
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.stage, url]);

  const onError = () => {
    if (current.stage === 'proxy') {
      markProxyBlocked();
      setSource({ key: url || '', src: null, stage: 'resolving' });
    } else if (current.stage === 'direct') {
      setSource({ key: url || '', src: null, stage: 'failed' });
    }
  };

  return { src: current.src, onError, failed: current.stage === 'failed', loading: current.stage === 'resolving' };
};

export const useRedGifsPoster = (url: string | null | undefined) =>
  useRedGifsSource(url, m => m.poster, id => `/api/thumbnail/${id}`);

